import React, { useEffect, useState } from "react";
import { ClockCircleOutlined, UserOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Divider,
  Input,
  Popover,
  Select,
  Table,
  Tooltip,
} from "antd";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";

import { DOWNLOAD_FILE_CLIENT } from "../../../../../../api/apiRoutes";
import { getListeModeleLettreMission } from "../../../../../../data/file";
import { cleanAndNormalizeName } from "../../../../../utilities/Utilities";
import SearchRow from "../../../../../common/SearchRow";
import FormLabel from "../../../../../common/Form/FormLabel";
import {
  FormContainer,
  TagItem,
} from "../../../../../common/common.components";
import { appTheme } from "../../../../../../config/theme";
import TableTitle from "../../../../../common/TableTitle";
import { getIconForFileType } from "../../../../../layout/menu/CustomMenu";
import useUsers from "../../../../../../hooks/users/useUser";
import { getClientBogListeModeleFile } from "../../../../../../data/bogClient";

const ListeModeleDocuments = ({ client, mission, codeSommaire }) => {
  // États
  const users = useUsers();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [filtersItems, setFiltersItems] = useState([]);
  const [paramsRecherche, setParamsRecherche] = useState({
    infoFile: "",
    user_id: "",
  });
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  // Effets
  useEffect(() => {
    if (client?.client_bog_id) {
      fetchDocumentsClient(client.client_bog_id);
    }
  }, [client]);

  // Fonctions utilitaires
  const fetchDocumentsClient = async (client_id) => {
    setLoading(true);
    try {
      //const res = await getListeModeleLettreMission(client_id);
      //const filter = res.result.filter((item) => item.codeSommaire === "EC1");

      const { result } = await getClientBogListeModeleFile(client_id);
      const resFiltered = (result ?? []).sort((p1, p2) => {
        return new Date(p2.createdAt) - new Date(p1.createdAt);
      });
      const filter = resFiltered.filter((item) => item.codeSommaire === "EC1");

      setFiles(filter || []);
      setFiltersItems(filter || []);
    } catch (error) {
      console.error("Erreur lors du chargement des documents:", error);
      setFiles([]);
      setFiltersItems([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadDocumentsClient = (clientBog, fichier) => {
    const clientBogName = cleanAndNormalizeName(clientBog);
    window.open(
      //`${DOWNLOAD_FILE_CLIENT}/${clientBogName}/MODELES/LETTRE-MISSION/${fichier}`,
      `${DOWNLOAD_FILE_CLIENT}/${clientBogName}/MODELES/SOMMAiRE/${encodeURIComponent(
        fichier
      )}`,
      "_blank"
    );
  };

  const handleSearch = () => {
    setLoading(true);

    setTimeout(() => {
      const filteredFiles = files.filter((file) => {
        const searchTerm = paramsRecherche.infoFile.toLowerCase();
        const textMatch = [file.reference, file.libelle, file.description].some(
          (field) => field?.toLowerCase().includes(searchTerm)
        );

        const userMatch =
          !paramsRecherche.user_id ||
          file.Lea_User?.id === paramsRecherche.user_id;

        return textMatch && userMatch;
      });

      setFiltersItems(filteredFiles);
      setLoading(false);
    }, 500);
  };

  const handleReset = () => {
    setParamsRecherche({
      infoFile: "",
      user_id: "",
    });
    setFiltersItems(files);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  // Configuration des colonnes
  const columns = [
    {
      title: () => <TableTitle>#</TableTitle>,
      render: (_, record, index) => index + 1,
      width: 50,
    },
    {
      title: () => <TableTitle>Référence</TableTitle>,
      dataIndex: "reference",
      render: (_, record) => <TagItem>{record.reference || "N/A"}</TagItem>,
    },
    {
      title: () => <TableTitle>Document</TableTitle>,
      dataIndex: "libelle",
      render: (_, record) => (
        <div className="d-flex align-items-center">
          <span className="svg-icon svg-icon-2x svg-icon-primary me-3">
            {getIconForFileType(`${record.libelle}.${record.extension || ""}`)}
          </span>
          <div className="fw-bold fs-7">{record.libelle}</div>
        </div>
      ),
    },
    {
      title: () => <TableTitle>Taille (Ko)</TableTitle>,
      dataIndex: "size",
      render: (_, record) => (
        <TagItem>{record.size ? (record.size / 1024).toFixed(2) : "0"}</TagItem>
      ),
      width: 100,
    },
    {
      title: () => <TableTitle>Dernière modification</TableTitle>,
      dataIndex: "updatedAt",
      render: (_, record) => (
        <div className="d-flex flex-column">
          <span className="text-muted fs-8 mb-1">
            <UserOutlined className="me-1" />
            {record.Lea_User
              ? `${record.Lea_User.nom_user} ${record.Lea_User.prenom_user}`
              : "Inconnu"}
          </span>
          <span className="text-primary fs-8 fw-semibold">
            <ClockCircleOutlined className="me-1" />
            {moment(record.updatedAt).format("DD/MM/YYYY à HH:mm")}
          </span>
        </div>
      ),
    },
    {
      title: () => <TableTitle>Notes</TableTitle>,
      dataIndex: "description",
      render: (_, record) =>
        record.description && (
          <Popover
            placement="topLeft"
            title="Notes"
            content={
              <div style={{ maxWidth: 300 }}>
                <Divider className="my-2" />
                <p className="fs-7 mb-0">{record.description}</p>
              </div>
            }
          >
            <Button
              size="small"
              type="text"
              icon={<FontAwesomeIcon icon={faExclamationCircle} />}
            />
          </Popover>
        ),
      width: 80,
    },
    {
      title: () => <TableTitle>Action</TableTitle>,
      render: (_, record) => (
        <Tooltip title="Télécharger le fichier">
          <Button
            type="primary"
            shape="circle"
            size="small"
            style={{ backgroundColor: appTheme.colors.primary }}
            onClick={() =>
              downloadDocumentsClient(
                record.Bog_Client?.nom_cabinet,
                record.fichier
              )
            }
            icon={<FontAwesomeIcon icon={faDownload} />}
          />
        </Tooltip>
      ),
      width: 80,
    },
  ];

  return (
    <>
      <SearchRow
        handleSearch={handleSearch}
        handleReset={handleReset}
        loading={loading}
      >
        <Col span={12}>
          <FormLabel label="Recherche" />
          <Input
            size="large"
            placeholder="Référence, libellé ou description..."
            value={paramsRecherche.infoFile}
            onChange={(e) =>
              setParamsRecherche({
                ...paramsRecherche,
                infoFile: e.target.value,
              })
            }
            allowClear
          />
        </Col>
        <Col span={12}>
          <FormLabel label="Utilisateur" />
          <Select
            placeholder="Sélectionner un utilisateur"
            size="large"
            style={{ width: "100%" }}
            value={paramsRecherche.user_id || undefined}
            allowClear
            onChange={(value) =>
              setParamsRecherche({
                ...paramsRecherche,
                user_id: value || "",
              })
            }
            options={
              users?.users?.map((user) => ({
                value: user.id,
                label: `${
                  user.initial || ""
                } - ${user.nom_user?.toUpperCase()} ${
                  user.prenom_user?.toUpperCase() || ""
                }`,
              })) || []
            }
          />
        </Col>
      </SearchRow>

      <FormContainer>
        <div className="py-4">
          <Table
            columns={columns}
            rowKey={(record) => record.id}
            dataSource={filtersItems}
            //pagination={tableParams.pagination}
            pagination={{
              ...tableParams.pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} éléments`,
            }}
            loading={loading}
            onChange={handleTableChange}
            size={"small"}
            scroll={{ x: 800 }}
          />
        </div>
      </FormContainer>
    </>
  );
};

export { ListeModeleDocuments };
